import { PROGRESS_GOLD } from '@sorrem-client/ui/shared/palette';
import { Button, Panel, ResourceBar } from '@sorrem-client/ui/components';
import type { ReactNode } from 'react';
import styles from './LoadingPage.module.scss';
import { useLoadingTip } from './use-loading-tip';


interface LoadingPageProps {
    /** 0 to 1. */
    progress: number;
    /** A line under the bar, such as who the run is waiting for or an error and its way out. */
    children?: ReactNode;
}

/**
 * The one loading view on the way into a crucible: from Character Home or a
 * cutscene to the gathering, and from the gathering to the run. Opaque, so
 * the scene loading underneath never shows half built. Sits under a
 * cutscene, which plays over it while the crucible loads. Shows a tip the
 * player can step through; the loading itself waits long enough for one to
 * be read (`LOADING_TIP_READ_MS`).
 */
export function LoadingPage({ progress, children }: LoadingPageProps): JSX.Element {
    const clampedProgress = Math.min(1, Math.max(0, progress));
    const { tip, position, count, showNext, showPrevious } = useLoadingTip();

    return (
        <div className={styles.loadingPage}>
            <div className={styles.barSlot}>
                <ResourceBar
                    value={Math.round(clampedProgress * 100)}
                    maxValue={100}
                    color={PROGRESS_GOLD}
                    label="Loading"
                    ariaLabel="Loading"
                />
                {children !== undefined && <div className={styles.status}>{children}</div>}
            </div>
            <section className={styles.tip} aria-label="Tip">
                <Panel className={styles.tipPanel}>
                    <p className={styles.tipHeading}>Tip {position} of {count}</p>
                    <p className={styles.tipText} aria-live="polite">{tip.text}</p>
                    <div className={styles.tipActions}>
                        <Button variant="secondary" size="small" onClick={showPrevious}>Previous tip</Button>
                        <Button variant="secondary" size="small" onClick={showNext}>Next tip</Button>
                    </div>
                </Panel>
            </section>
        </div>
    );
}
